import { useState } from "react";

import { faEmptySet } from "@fortawesome/pro-regular-svg-icons";
import { faChevronDown, faSearch } from "@fortawesome/pro-solid-svg-icons";
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from "@headlessui/react";
import { AnimatePresence } from "framer-motion";
import { IFuseOptions } from "fuse.js";
import { times } from "lodash";

import { Icon } from "src/base-components/Icon";
import { Input } from "src/base-components/Input";
import { MotionDropdownPanel } from "src/base-components/MotionDropdownPanel";
import { Skeleton } from "src/base-components/Skeleton";
import { DropdownItem } from "src/design-system/Dropdown";
import { EmptyState } from "src/design-system/EmptyState";
import { useFuseSearch } from "src/utils/useFuseSearch";

type Option<V> = {
  key: string;
  value: V;
  disabled?: boolean;
};

type SearchableSelectProps<V = string> = {
  selected?: Nullable<string>;
  onChange: (key: string, value: V) => void;
  placeholder?: string;
  buttonRenderer?: (args: ButtonProps<V>) => React.ReactNode;
  itemRenderer?: (args: ItemProps<V>) => React.ReactNode;
  skeletonRenderer?: (index: number) => React.ReactNode;
  searchPlaceholder?: string;
  isLoading?: boolean;
  emptyState?: React.ReactNode;
  options: Option<V>[];
  dataLoc?: string;
  searchOptions?: IFuseOptions<Option<V>>;
  disabled?: boolean;
  buttonAs?: React.ElementType;
};

export const SearchableSelect = <V,>({
  options,
  selected,
  onChange,
  placeholder = "",
  buttonRenderer = defaultButtonRenderer,
  itemRenderer = defaultItemRenderer,
  skeletonRenderer = defaultSkeletonRenderer,
  searchPlaceholder = "Search...",
  isLoading,
  emptyState = DEFAULT_EMPTY_STATE,
  dataLoc,
  searchOptions = {},
  disabled = false,
  buttonAs = "button",
}: SearchableSelectProps<V>) => {
  const [query, setQuery] = useState("");

  const search = useFuseSearch(options, {
    ...searchOptions,
    keys: searchOptions.keys
      ? searchOptions.keys.map((key) => `value.${key}`)
      : ["value"],
  });

  const results = search(query);
  const selectedValue =
    options.find((option) => option.key === selected) ?? null;

  return (
    <Listbox
      disabled={disabled}
      value={selectedValue}
      onChange={(option) => {
        if (!option) return;

        onChange?.(option.key, option.value);
      }}
    >
      {({ open }) => (
        <>
          <ListboxButton
            as={buttonAs}
            className="flex"
            data-loc={dataLoc}
            disabled={disabled}
          >
            {buttonRenderer({
              value: selectedValue?.value,
              key: selectedValue?.key,
              placeholder,
              isOpen: open,
            })}
          </ListboxButton>
          <AnimatePresence initial={false} onExitComplete={() => setQuery("")}>
            {open && (
              <ListboxOptions
                anchor={{
                  to: "bottom start",
                  offset: 8,
                  padding: 8,
                  gap: 6,
                }}
                as={MotionDropdownPanel}
                className="min-h-[18rem] min-w-[max(var(--button-width),17rem)] max-w-80"
                data-loc={`${dataLoc}-options`}
                static
              >
                <div className="px-4 py-2">
                  <Input
                    ref={(node) => node?.focus()} //Focus on first render
                    disabled={isLoading}
                    placeholder={searchPlaceholder}
                    prefixIcon={{ icon: faSearch }}
                    role="textbox"
                    value={query}
                    fullWidth
                    onChange={(e) => setQuery(e.target.value)}
                  />
                </div>
                <div className="decideScrollbar max-h-60 overflow-y-auto">
                  {(() => {
                    if (isLoading) {
                      return times(6, skeletonRenderer);
                    }

                    return results.length > 0 ? (
                      <ul>
                        {results.map((item, index) => (
                          <ListboxOption
                            key={item.key}
                            as={DropdownItem}
                            disabled={item.disabled}
                            value={item}
                          >
                            {itemRenderer({
                              key: item.key,
                              value: item.value,
                              index,
                            })}
                          </ListboxOption>
                        ))}
                      </ul>
                    ) : (
                      emptyState
                    );
                  })()}
                </div>
              </ListboxOptions>
            )}
          </AnimatePresence>
        </>
      )}
    </Listbox>
  );
};

// eslint-disable-next-line prefer-arrow/prefer-arrow-functions
function assertValue(value: unknown): asserts value is string {
  if (typeof value !== "string" && value !== undefined) {
    throw new Error(
      "Looks like you're using non-string value. You need to specify custom `button` and `item` renderers.",
    );
  }
}

type ButtonProps<V> = {
  key: string | undefined;
  value: V | undefined;
  placeholder: string;
  isOpen: boolean;
};

const defaultButtonRenderer = <V,>({ value, placeholder }: ButtonProps<V>) => {
  assertValue(value);

  return (
    <div className="flex items-center gap-x-1 font-inter-normal-12px text-gray-800">
      {value ?? placeholder}
      <Icon color="text-gray-500" icon={faChevronDown} size="3xs" />
    </div>
  );
};

type ItemProps<V> = {
  index: number;
  key: string;
  value: V;
};

const defaultItemRenderer = <V,>({ value }: ItemProps<V>) => {
  assertValue(value);

  return <span className="font-inter-normal-13px text-gray-800">{value}</span>;
};

const defaultSkeletonRenderer = (index: number) => (
  <div
    key={index}
    className="flex items-center gap-x-2 px-4 py-2 font-inter-normal-13px"
  >
    <Skeleton />
  </div>
);

const DEFAULT_EMPTY_STATE = (
  <EmptyState
    description="Change your search query to try again"
    headline="No items found"
    icon={faEmptySet}
  />
);
